export type PanelAttachment = 'pinned' | 'floating';

export interface PanelViewport {
    width: number;
    height: number;
}

export interface PanelRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface PanelSessionState {
    isOpen: boolean;
    attachment: PanelAttachment;
    pinnedWidth: number;
    floating: PanelRect;
    activePanelId: string;
}

export interface PanelStateStorage {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem?: (key: string) => void;
}

export type PanelSessionStateUpdate = Partial<PanelSessionState> | ((currentState: PanelSessionState) => PanelSessionState | Partial<PanelSessionState>);

export interface DefaultPanelSessionStateOptions {
    attachment?: PanelAttachment;
    floatingMaxHeight?: number;
    floatingViewportHeightOffset?: number;
    isOpen: boolean;
    panelId: string;
    pinnedWidth: number;
    viewport?: PanelViewport;
    x?: number;
    y?: number;
}

export const PANEL_SESSION_STORAGE_KEY = 'vplant.panel-session-state.v1';
export const PANEL_MIN_WIDTH = 280;
export const PANEL_MAX_WIDTH = 640;
export const PANEL_MIN_HEIGHT = 260;
export const PANEL_MAX_HEIGHT = 900;
export const PANEL_MARGIN = 8;
export const PANEL_MIN_VISIBLE_SIZE = 160;

const FALLBACK_VIEWPORT: PanelViewport = { width: 1024, height: 768 };

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
};

const toFiniteNumber = (value: unknown, fallback: number): number => {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
};

const getSizeBounds = (minimum: number, maximum: number, available: number) => {
    const adjustedAvailable = Math.max(PANEL_MIN_VISIBLE_SIZE, available);
    const min = Math.min(minimum, adjustedAvailable);
    const max = Math.max(min, Math.min(maximum, adjustedAvailable));
    return { min, max };
};

export const clampNumber = (value: number, min: number, max: number): number => {
    return Math.min(Math.max(value, min), max);
};

export const getWindowViewport = (): PanelViewport => {
    if (typeof window === 'undefined') {
        return FALLBACK_VIEWPORT;
    }

    return {
        width: window.innerWidth || FALLBACK_VIEWPORT.width,
        height: window.innerHeight || FALLBACK_VIEWPORT.height,
    };
};

export const getSessionPanelStorage = (): PanelStateStorage | null => {
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            return window.sessionStorage;
        }
    } catch {
        return null;
    }

    return null;
};

export const createDefaultPanelSessionState = (options: DefaultPanelSessionStateOptions): PanelSessionState => {
    const viewport = options.viewport ?? getWindowViewport();
    const floatingMaxHeight = options.floatingMaxHeight ?? 720;
    const floatingViewportHeightOffset = options.floatingViewportHeightOffset ?? 120;

    return {
        isOpen: options.isOpen,
        attachment: options.attachment ?? 'pinned',
        pinnedWidth: options.pinnedWidth,
        floating: {
            x: options.x ?? 72,
            y: options.y ?? 72,
            width: Math.min(options.pinnedWidth, Math.max(PANEL_MIN_VISIBLE_SIZE, viewport.width - (PANEL_MARGIN * 2))),
            height: Math.min(floatingMaxHeight, Math.max(PANEL_MIN_HEIGHT, viewport.height - floatingViewportHeightOffset)),
        },
        activePanelId: options.panelId,
    };
};

export const normalizePanelSessionState = (
    value: unknown,
    defaults: PanelSessionState,
    viewport: PanelViewport = getWindowViewport()
): PanelSessionState => {
    const source = isRecord(value) ? value : {};
    const attachment = source.attachment === 'floating' || source.attachment === 'pinned'
        ? source.attachment
        : defaults.attachment;

    const floatingSource = isRecord(source.floating) ? source.floating : {};
    const availableWidth = viewport.width - (PANEL_MARGIN * 2);
    const availableHeight = viewport.height - (PANEL_MARGIN * 2);
    const widthBounds = getSizeBounds(PANEL_MIN_WIDTH, availableWidth, availableWidth);
    const heightBounds = getSizeBounds(PANEL_MIN_HEIGHT, availableHeight, availableHeight);

    const floatingWidth = clampNumber(
        toFiniteNumber(floatingSource.width, defaults.floating.width),
        widthBounds.min,
        widthBounds.max
    );
    const floatingHeight = clampNumber(
        toFiniteNumber(floatingSource.height, defaults.floating.height),
        heightBounds.min,
        heightBounds.max
    );

    const maxX = Math.max(PANEL_MARGIN, viewport.width - floatingWidth - PANEL_MARGIN);
    const maxY = Math.max(PANEL_MARGIN, viewport.height - floatingHeight - PANEL_MARGIN);

    return {
        isOpen: typeof source.isOpen === 'boolean' ? source.isOpen : defaults.isOpen,
        attachment,
        pinnedWidth: clampNumber(
            toFiniteNumber(source.pinnedWidth, defaults.pinnedWidth),
            widthBounds.min,
            widthBounds.max
        ),
        floating: {
            x: clampNumber(toFiniteNumber(floatingSource.x, defaults.floating.x), PANEL_MARGIN, maxX),
            y: clampNumber(toFiniteNumber(floatingSource.y, defaults.floating.y), PANEL_MARGIN, maxY),
            width: floatingWidth,
            height: floatingHeight,
        },
        activePanelId: typeof source.activePanelId === 'string' && source.activePanelId
            ? source.activePanelId
            : defaults.activePanelId,
    };
};

export const readPanelSessionStateMap = (storage: PanelStateStorage | null = getSessionPanelStorage()): Record<string, unknown> => {
    if (!storage) {
        return {};
    }

    try {
        const rawValue = storage.getItem(PANEL_SESSION_STORAGE_KEY);
        if (!rawValue) {
            return {};
        }

        const parsedValue = JSON.parse(rawValue);
        return isRecord(parsedValue) ? parsedValue : {};
    } catch {
        return {};
    }
};

export const writePanelSessionStateMap = (
    stateMap: Record<string, unknown>,
    storage: PanelStateStorage | null = getSessionPanelStorage()
): void => {
    if (!storage) {
        return;
    }

    storage.setItem(PANEL_SESSION_STORAGE_KEY, JSON.stringify(stateMap));
};

export const loadPanelSessionState = (
    panelId: string,
    defaults: PanelSessionState,
    storage: PanelStateStorage | null = getSessionPanelStorage(),
    viewport: PanelViewport = getWindowViewport()
): PanelSessionState => {
    const stateMap = readPanelSessionStateMap(storage);
    return normalizePanelSessionState(stateMap[panelId], defaults, viewport);
};

export const savePanelSessionState = (
    panelId: string,
    state: PanelSessionState,
    storage: PanelStateStorage | null = getSessionPanelStorage()
): PanelSessionState => {
    const stateMap = readPanelSessionStateMap(storage);
    stateMap[panelId] = state;
    writePanelSessionStateMap(stateMap, storage);
    return state;
};

export const updatePanelSessionState = (
    panelId: string,
    defaults: PanelSessionState,
    update: PanelSessionStateUpdate,
    storage: PanelStateStorage | null = getSessionPanelStorage(),
    viewport: PanelViewport = getWindowViewport()
): PanelSessionState => {
    const currentState = loadPanelSessionState(panelId, defaults, storage, viewport);
    const updateValue = typeof update === 'function' ? update(currentState) : update;
    const nextValue = { ...currentState, ...updateValue };
    const normalizedState = normalizePanelSessionState(nextValue, defaults, viewport);
    return savePanelSessionState(panelId, normalizedState, storage);
};